import { typographyCss } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils } from 'common/hooks';

import InputMonetary from './InputMonetary';
import { FormValues } from './types';
import { getPercentageDiscountPrice } from '../utils';

type Props = {
  currency: Currency;
  disabled: boolean;
  isWeightedItem: boolean;
};

const BREAKPOINT = 440;
const INPUT_WIDTH = 120;

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${BREAKPOINT}px) {
    flex-direction: row;
  }
`;

const DiscountTypeSelect = styled(Select)`
  max-width: 392px;
  margin-block-end: 12px;

  @media (min-width: ${BREAKPOINT}px) {
    width: 256px;
    margin-inline-end: 16px;
    margin-block-end: 0;
  }
`;

const FixedPriceInput = styled(InputMonetary)`
  width: ${INPUT_WIDTH}px;
`;

const PercentageInput = styled(InputNumber)`
  width: ${INPUT_WIDTH}px;
`;

const DisabledEmptyInput = styled(Input).attrs({ disabled: true })``;

const PercentageDiscountPreview = styled.div`
  margin-block-start: 12px;
  ${typographyCss.Small}
`;

const DiscountFields = ({ currency, disabled, isWeightedItem }: Props) => {
  const intl = useIntl();
  const { getCurrencySymbol, getLocalizedUnit } = useLocaleUtils();

  const basePriceField = useField<FormValues['price']>('price');
  const fixedDiscountPriceField = useField<FormValues['fixedDiscountPrice']>('fixedDiscountPrice');
  const discountPercentageField = useField<FormValues['discountPercentage']>('discountPercentage');
  const discountTypeField = useField<FormValues['discountType']>('discountType');

  const percentageDiscountPrice = useMemo(() => {
    return basePriceField.meta.valid &&
      discountPercentageField.meta.valid &&
      typeof basePriceField.input.value === 'number' &&
      typeof discountPercentageField.input.value === 'number'
      ? getPercentageDiscountPrice(basePriceField.input.value, discountPercentageField.input.value, currency)
      : undefined;
  }, [basePriceField, currency, discountPercentageField]);

  const discountTypeOptions: { value: 'absolute' | 'relative' | ''; label: string }[] = useMemo(
    () => [
      { value: '', label: intl.formatMessage({ id: 'menu-item.no-discount' }) },
      {
        value: 'absolute',
        label: intl.formatMessage({ id: 'menu-item.discounted-price' }),
      },
      { value: 'relative', label: intl.formatMessage({ id: 'menu-item.discount-percentage' }) },
    ],
    [intl],
  );

  return (
    <div>
      <InputsWrapper>
        <RFFFieldErrorWrapper field={discountTypeField}>
          {input => (
            <DiscountTypeSelect
              {...input}
              clearable
              disabled={disabled}
              onChange={option => {
                fixedDiscountPriceField.input.onChange(undefined);
                discountPercentageField.input.onChange(undefined);
                input.onChange(option ?? '');
              }}
              options={discountTypeOptions}
            />
          )}
        </RFFFieldErrorWrapper>

        {discountTypeField.input.value === 'relative' ? (
          <RFFFieldErrorWrapper field={discountPercentageField} showErrorImmediately>
            {input => <PercentageInput {...input} disabled={disabled} suffixText="%" />}
          </RFFFieldErrorWrapper>
        ) : discountTypeField.input.value === 'absolute' ? (
          <RFFFieldErrorWrapper field={fixedDiscountPriceField} showErrorImmediately>
            {input =>
              isWeightedItem ? (
                <InputMonetary
                  {...input}
                  disabled={disabled}
                  suffixText={`${getCurrencySymbol(currency)}/${getLocalizedUnit('kilogram')}`}
                  currency={currency}
                />
              ) : (
                <FixedPriceInput {...input} disabled={disabled} currency={currency} />
              )
            }
          </RFFFieldErrorWrapper>
        ) : (
          <DisabledEmptyInput />
        )}
      </InputsWrapper>
      {discountTypeField.input.value === 'relative' && (
        <PercentageDiscountPreview>
          <FormattedMessage id="menu-item.discount-percentage-final-price" />
          {percentageDiscountPrice ? (
            <>
              <MonetaryAmount amount={parseFloat(percentageDiscountPrice)} currency={currency} />
              {isWeightedItem && `/${getLocalizedUnit('kilogram')}`}
            </>
          ) : (
            '-'
          )}
        </PercentageDiscountPreview>
      )}
    </div>
  );
};

export default DiscountFields;
